import type { DeliveryState, PayloadSnapshot, Row } from './types'

export const PAYDOWN_STATES: Exclude<DeliveryState, 'discarded'>[] = ['deployed', 'pending', 'nochg', 'gap']
export const DELIVERED_RESOLUTIONS = ['Done']
export const DISCARDED_RESOLUTIONS = [
  'Canceled', 'Cancelled', 'Duplicate', "Won't Do", 'Wont Do', 'Cannot Reproduce', 'Incomplete', 'Not a Bug',
]

/**
 * A Jira resolution outside DELIVERED_RESOLUTIONS/DISCARDED_RESOLUTIONS. Resolution names are per-project
 * admin config, so a new one can appear at any time; callers building rows for many issues should catch
 * this and exclude the single offending issue rather than let it fail an entire batch.
 */
export class UnrecognizedResolutionError extends Error {
  constructor(readonly resolution: string) {
    super(`Unrecognized resolution: ${resolution}`)
    this.name = 'UnrecognizedResolutionError'
  }
}

export interface DeliveryLink {
  type?: { name?: string | null } | null
  inwardIssue?: { key?: string | null } | null
  outwardIssue?: { key?: string | null } | null
}

export interface ChangeRecordLike {
  cat?: string | null
  actual_end?: string | null
  resolved?: string | null
}

export interface ProjectChangePractice {
  chgs?: number | null
  issues?: number | null
}

export interface DeliveryResolution {
  state: DeliveryState | null
  chgs: string[]
  chgClosed: string[]
  releasers: string[]
  deployedOn: string | null
  resolution: string | null
  projectChanges: number
  projectIssues: number
  projectManaged: boolean
}

export function linkedChanges(links: DeliveryLink[] | null | undefined, changeProject = 'CHG'): { chgs: string[], releasers: string[] } {
  const chgs = new Set<string>()
  const releasers = new Set<string>()
  ;(links || []).forEach((link) => {
    if (!link.type || link.type.name !== 'Releases') return
    const key = (link.inwardIssue || link.outwardIssue || {}).key
    if (!key || key.indexOf('-') < 0) return
    if (key.slice(0, key.lastIndexOf('-')) === changeProject) chgs.add(key)
    else releasers.add(key)
  })
  return { chgs: Array.from(chgs).sort(), releasers: Array.from(releasers).sort() }
}

export function deployDate(keys: string[], changes: Record<string, ChangeRecordLike>): string | null {
  let latest: string | null = null
  keys.forEach((key) => {
    const change = changes[key]
    if (!change || change.cat !== 'done') return
    const date = change.actual_end || change.resolved
    if (date && (!latest || date.slice(0, 10) > latest)) latest = date.slice(0, 10)
  })
  return latest
}

export function projectIsChangeManaged(
  project: string,
  practice: Record<string, ProjectChangePractice>,
  hasLinkedItem: boolean,
  activeMinimum = 5,
): boolean {
  const changes = practice[project] ? practice[project].chgs || 0 : 0
  return hasLinkedItem || changes >= activeMinimum
}

export function resolveDelivery(
  open: boolean,
  resolution: string | null,
  project: string,
  links: DeliveryLink[] | null | undefined,
  changes: Record<string, ChangeRecordLike>,
  practice: Record<string, ProjectChangePractice>,
  hasLinkedItem: boolean,
  activeMinimum = 5,
  changeProject = 'CHG',
): DeliveryResolution {
  const linked = linkedChanges(links, changeProject)
  if (resolution && DELIVERED_RESOLUTIONS.indexOf(resolution) < 0 && DISCARDED_RESOLUTIONS.indexOf(resolution) < 0) {
    throw new UnrecognizedResolutionError(resolution)
  }
  const closed = linked.chgs.filter((key) => changes[key] && changes[key].cat === 'done')
  const managed = projectIsChangeManaged(project, practice, hasLinkedItem, activeMinimum)
  const state: DeliveryState | null = open || !resolution
    ? null
    : DISCARDED_RESOLUTIONS.indexOf(resolution) >= 0
      ? 'discarded'
      : closed.length
        ? 'deployed'
        : linked.chgs.length
          ? 'pending'
          : managed
            ? 'gap'
            : 'nochg'
  const projectPractice = practice[project] || {}
  return {
    state,
    chgs: linked.chgs,
    chgClosed: closed,
    releasers: linked.releasers,
    deployedOn: closed.length ? deployDate(linked.chgs, changes) : null,
    resolution,
    projectChanges: projectPractice.chgs || 0,
    projectIssues: projectPractice.issues || 0,
    projectManaged: managed,
  }
}

export function daysAgo(value: string | null, asOf: string): number | null {
  if (!value) return null
  return Math.max(0, Math.round((Date.parse(`${asOf}T00:00:00Z`) - Date.parse(`${value.slice(0, 10)}T00:00:00Z`)) / 86400000))
}

export function deliveredIn(rows: Row[], window: number, asOf: string): Row[] {
  return rows.filter((row) => PAYDOWN_STATES.indexOf(row.dstate as Exclude<DeliveryState, 'discarded'>) >= 0
    && (daysAgo(row.resolved, asOf) || 0) <= window && daysAgo(row.resolved, asOf) !== null)
}

export function discardedIn(rows: Row[], window: number, asOf: string): Row[] {
  return rows.filter((row) => row.dstate === 'discarded'
    && (daysAgo(row.resolved, asOf) || 0) <= window && daysAgo(row.resolved, asOf) !== null)
}

export function deliveredPrevious(rows: Row[], window: number, asOf: string): Row[] {
  return rows.filter((row) => {
    const age = daysAgo(row.resolved, asOf)
    return PAYDOWN_STATES.indexOf(row.dstate as Exclude<DeliveryState, 'discarded'>) >= 0
      && age !== null && age > window && age <= window * 2
  })
}

export function stateCounts(rows: Row[], states: DeliveryState[]): Record<DeliveryState, number> {
  const counts = {} as Record<DeliveryState, number>
  states.forEach((state) => { counts[state] = 0 })
  rows.forEach((row) => {
    if (row.dstate) counts[row.dstate] = (counts[row.dstate] || 0) + 1
  })
  return counts
}

export interface DeliveryMonth {
  month: string
  rows: Row[]
  counts: Record<DeliveryState, number>
  delivered: number
}

export function deliveryMonths(
  rows: Row[],
  payload: Pick<PayloadSnapshot, 'as_of' | 'first_label' | 'deliv_order'>,
): DeliveryMonth[] {
  const byMonth = new Map<string, Row[]>()
  rows.forEach((row) => {
    if (!row.resolved || (PAYDOWN_STATES.indexOf(row.dstate as Exclude<DeliveryState, 'discarded'>) < 0 && row.dstate !== 'discarded')) return
    const month = row.resolved.slice(0, 7)
    if (payload.first_label && month < payload.first_label.slice(0, 7)) return
    const existing = byMonth.get(month) || []
    existing.push(row)
    byMonth.set(month, existing)
  })
  if (!byMonth.size) return []

  const first = Array.from(byMonth.keys()).sort()[0]
  const [year, month] = first.split('-').map(Number)
  const end = payload.as_of.slice(0, 7)
  const output: DeliveryMonth[] = []
  let currentYear = year
  let currentMonth = month
  while (true) {
    if (currentMonth > 12) {
      currentYear++
      currentMonth = 1
    }
    const key = `${currentYear}-${currentMonth < 10 ? `0${currentMonth}` : currentMonth}`
    const entries = byMonth.get(key) || []
    output.push({
      month: key,
      rows: entries,
      counts: stateCounts(entries, payload.deliv_order),
      delivered: entries.filter((row) => PAYDOWN_STATES.indexOf(row.dstate as Exclude<DeliveryState, 'discarded'>) >= 0).length,
    })
    if (key >= end) break
    currentMonth++
  }
  return output
}
